import { BarChart } from '@mantine/charts';
import { Card, Center, Grid, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { IconUser, IconUsers } from '@tabler/icons-react';
import PieChart from './PieChart';

const CustomerTypeRevenueChart = ({ data, currency, loading }) => {
  if (loading || data === undefined) {
    return (
      <Card padding="md" radius="md" withBorder>
        <Title order={4} ta="center" mb="md">
          New vs Repeat Customers
        </Title>
        <Center h={200}>
          <Loader type="oval" />
        </Center>
      </Card>
    );
  }

  if (!data) return null;

  const { newCustomers, repeatCustomers, totals } = data;

  // Check if we have any data
  const hasData =
    (newCustomers?.value?.length > 0 || repeatCustomers?.value?.length > 0) &&
    (totals?.newCustomers?.count > 0 || totals?.repeatCustomers?.count > 0);

  if (!hasData) {
    return (
      <Card padding="md" radius="md" withBorder>
        <Title order={4} ta="center" mb="md">
          New vs Repeat Customers
        </Title>
        <Text ta="center" c="dimmed">
          No purchase data available for the selected period
        </Text>
      </Card>
    );
  }

  // Transform data for stacked bar chart (revenue over time)
  // Combine new and repeat customer data by date
  const allDates = new Set([
    ...(newCustomers?.value?.map((d) => d.x) || []),
    ...(repeatCustomers?.value?.map((d) => d.x) || []),
  ]);

  const chartData = Array.from(allDates)
    .sort()
    .map((date) => {
      const newValue = newCustomers?.value?.find((d) => d.x === date)?.y || 0;
      const repeatValue = repeatCustomers?.value?.find((d) => d.x === date)?.y || 0;
      return {
        date,
        'New Customers': newValue,
        'Repeat Customers': repeatValue,
      };
    });

  // Pie chart data for revenue totals
  const revenuePieData = [
    {
      label: 'New Customers',
      value: totals?.newCustomers?.value || 0,
      color: 'var(--mantine-color-primary-filled)',
    },
    {
      label: 'Repeat Customers',
      value: totals?.repeatCustomers?.value || 0,
      color: 'var(--mantine-color-gray-5)',
    },
  ];

  // Pie chart data for order count totals
  const countPieData = [
    {
      label: 'New Customers',
      value: totals?.newCustomers?.count || 0,
      color: 'var(--mantine-color-primary-filled)',
    },
    {
      label: 'Repeat Customers',
      value: totals?.repeatCustomers?.count || 0,
      color: 'var(--mantine-color-gray-5)',
    },
  ];

  const totalRevenue = (totals?.newCustomers?.value || 0) + (totals?.repeatCustomers?.value || 0);
  const totalOrders = (totals?.newCustomers?.count || 0) + (totals?.repeatCustomers?.count || 0);

  const newRevenuePercent = totalRevenue > 0 ? ((totals?.newCustomers?.value || 0) / totalRevenue) * 100 : 0;
  const repeatRevenuePercent = totalRevenue > 0 ? ((totals?.repeatCustomers?.value || 0) / totalRevenue) * 100 : 0;
  const newOrdersPercent = totalOrders > 0 ? ((totals?.newCustomers?.count || 0) / totalOrders) * 100 : 0;
  const repeatOrdersPercent = totalOrders > 0 ? ((totals?.repeatCustomers?.count || 0) / totalOrders) * 100 : 0;

  // Format X-axis labels: strip year from YYYY-MM-DD format
  const xAxisFormatter = (value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value.slice(5);
    }
    return value;
  };

  // Custom value formatter for currency
  const valueFormatter = (value) =>
    value?.toLocaleString(navigator.language, { style: 'currency', currency });

  const series = [
    { name: 'New Customers', color: 'var(--mantine-color-primary-filled)' },
    { name: 'Repeat Customers', color: 'var(--mantine-color-gray-5)' },
  ];

  return (
    <Card padding="md" radius="md" withBorder>
      <Title order={4} ta="center" mb="md">
        New vs Repeat Customers
      </Title>

      <Grid>
        {/* Summary Stats */}
        <Grid.Col span={12}>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Card withBorder padding="sm" radius="md">
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Group gap="xs">
                      <IconUser size={16} color="var(--mantine-color-primary-filled)" />
                      <Text size="sm" fw={500} c="dimmed">
                        New Customers
                      </Text>
                    </Group>
                    <Text size="xl" fw={700}>
                      {totals?.newCustomers?.count?.toLocaleString() || 0} orders
                    </Text>
                    <Text size="sm" c="dimmed">
                      {valueFormatter(totals?.newCustomers?.value || 0)} revenue
                    </Text>
                    <Text size="xs" c="violet">
                      {newOrdersPercent.toFixed(1)}% of orders | {newRevenuePercent.toFixed(1)}% of revenue
                    </Text>
                  </Stack>
                </Group>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Card withBorder padding="sm" radius="md">
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Group gap="xs">
                      <IconUsers size={16} color="var(--mantine-color-gray-5)" />
                      <Text size="sm" fw={500} c="dimmed">
                        Repeat Customers
                      </Text>
                    </Group>
                    <Text size="xl" fw={700}>
                      {totals?.repeatCustomers?.count?.toLocaleString() || 0} orders
                    </Text>
                    <Text size="sm" c="dimmed">
                      {valueFormatter(totals?.repeatCustomers?.value || 0)} revenue
                    </Text>
                    <Text size="xs" c="dimmed">
                      {repeatOrdersPercent.toFixed(1)}% of orders | {repeatRevenuePercent.toFixed(1)}% of revenue
                    </Text>
                  </Stack>
                </Group>
              </Card>
            </Grid.Col>
          </Grid>
        </Grid.Col>

        {/* Pie Charts */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack align="center" gap="xs">
            <Text size="sm" fw={500} c="dimmed">
              Revenue Distribution
            </Text>
            <PieChart data={revenuePieData} currency={currency} size={140} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack align="center" gap="xs">
            <Text size="sm" fw={500} c="dimmed">
              Orders Distribution
            </Text>
            <PieChart data={countPieData} size={140} />
          </Stack>
        </Grid.Col>

        {/* Stacked Bar Chart for Revenue Over Time */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack align="center" gap="xs">
            <Text size="sm" fw={500} c="dimmed">
              Revenue Over Time
            </Text>
            {chartData.length > 0 ? (
              <BarChart
                h={160}
                data={chartData}
                dataKey="date"
                series={series}
                type="stacked"
                withLegend={false}
                valueFormatter={valueFormatter}
                xAxisProps={{ tickFormatter: xAxisFormatter }}
                yAxisProps={{
                  tickFormatter: (value) =>
                    new Intl.NumberFormat(navigator.language, {
                      style: 'currency',
                      currency,
                      notation: 'compact',
                      maximumFractionDigits: 0,
                    }).format(value),
                }}
              />
            ) : (
              <Text c="dimmed" size="sm">
                No time series data
              </Text>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Card>
  );
};

export default CustomerTypeRevenueChart;
